import { queryKeys } from '@/lib/query-keys';
import { isLineInProgress } from '@/lib/utils';
import type {
} from '@octave/contracts';
import type {
  MobileLine,
  MobileLineConsumptionSummary,
  PaginationInfo,
} from '@octave/contracts';
import {
  keepPreviousData,
  useQuery,
  } from '@tanstack/react-query';
import {
  lineClient,
  } from '@/lib/api/ts-rest';
import { useDefaultSite } from './useDefaultSite';
import { useCurrentOrganization } from './useOrganization';


export const useLines = ({
  page,
  limit,
  filters,
  searchText,
}: {
  page: number;
  limit: number;
  filters?: {
    status?: string[];
    operator?: string[];
    type?: string[];
    plan?: string[];
  };
  searchText?: string;
}) => {
  const { data: organization } = useCurrentOrganization();
  const { currentSite } = useDefaultSite();

  return useQuery<
    { lines: MobileLine[]; pageInfo: PaginationInfo },
    Error
  >({
    queryKey: queryKeys.lines.list(currentSite?.id, organization?.id, {
      page,
      limit,
      filters,
      searchText,
    }),
    queryFn: async () => {
      if (!organization) {
        throw new Error('No organization or centre de facturation selected');
      }
      const query = {
        page,
        limit,
        q: searchText,
        status: filters?.status,
        operator: filters?.operator,
        type: filters?.type,
        plan: filters?.plan,
      };
      const res = currentSite
        ? await lineClient.listSiteLines({
            params: { organizationId: organization.id, siteId: currentSite.id },
            query,
          })
        : await lineClient.listOrganizationLines({
            params: { organizationId: organization.id },
            query,
          });
      if (res.status !== 200) throw new Error('Failed to load lines');
      return res.body;
    },
    enabled: !!organization,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};

export interface LinesSummary {
  total: number;
  statusCounts: Record<string, number>;
  networkStats: Array<{ name: string; count: number }>;
  planStats: Array<{ name: string; count: number }>;
  typeStats: Array<{ name: string; count: number }>;
  siteStats: Array<{ name: string; count: number }>;
  filterOptions: {
    status: string[];
    operator: string[];
    type: string[];
  };
  generatedAt: string;
}

export const useLinesSummary = () => {
  const { data: organization } = useCurrentOrganization();
  const { currentSite } = useDefaultSite();

  return useQuery<LinesSummary, Error>({
    queryKey: queryKeys.lines.summary(currentSite?.id, organization?.id),
    queryFn: async () => {
      if (!organization) {
        throw new Error('No organization or centre de facturation selected');
      }
      const res = currentSite
        ? await lineClient.getSiteLinesSummary({
            params: { organizationId: organization.id, siteId: currentSite.id },
          })
        : await lineClient.getOrganizationLinesSummary({
            params: { organizationId: organization.id },
          });
      if (res.status !== 200) throw new Error('Failed to load summary');
      return res.body;
    },
    enabled: !!organization,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000,
  });
};

export const useLine = (lineId: string | undefined) => {
  return useQuery<MobileLine, Error>({
    queryKey: queryKeys.lines.detail(lineId),
    queryFn: async () => {
      const res = await lineClient.getLine({ params: { lineId: lineId! } });
      if (res.status !== 200) throw new Error('Failed to load line');
      return res.body;
    },
    enabled: !!lineId,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && isLineInProgress(data) ? 5_000 : false;
    },
  });
};

export const useLineConsumptionSummary = (
  lineId: string | undefined,
  month: number,
  year: number
) => {
  return useQuery<MobileLineConsumptionSummary[], Error>({
    queryKey: queryKeys.lines.consumption(lineId, month, year),
    queryFn: async () => {
      const res = await lineClient.getLineConsumptionSummary({
        params: { lineId: lineId! },
        query: { month, year },
      });
      if (res.status !== 200) throw new Error('Failed to load consumption');
      return res.body;
    },
    enabled: !!lineId && !!month && !!year,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};
